import { logos } from '@pypestream/design-system';

import { stripLaapiPrefix } from './platform-catalog';

// Maps an `agent_routing` value to a KEY of `logos` rather than to the resolved
// logo itself. Resolving eagerly here would touch `logos` at module-load time,
// and this module is re-exported from the `src/utils` barrel — so every unit
// test that stubs `@pypestream/design-system` without a `logos` key crashed on
// import, long before reaching its own subject. Keep the deref inside the
// function.
// Exported for its test only: iterating EVERY value through the stubbed `logos`
// is what turns "these keys exist" into "no key points at a missing asset".
export const PLATFORM_LOGO_KEYS: Record<string, keyof typeof logos> = {
  cc: 'contactCenter',
  contact_center: 'contactCenter',
  contactcenter: 'contactCenter',
  salesforce: 'salesforce',
  quiq: 'quiq',
  genesys: 'genesys',
  amazon_connect: 'amazonConnect',
  service_now: 'serviceNow',
  servicenow: 'serviceNow',
  // Spellings the LAAPI integration names use once the prefix is stripped
  // (`LAAPI_AmazonConnect` -> `amazonconnect`); the two Salesforce connectors
  // share the Salesforce mark. Zendesk has no logo asset, so it keeps the
  // fallback below rather than borrowing another platform's mark.
  amazonconnect: 'amazonConnect',
  salesforceliveagent: 'salesforce',
  salesforcemessaging: 'salesforce',
};

export const getPlatformLogo = (agentRouting?: string): string => {
  // Match on the platform identity, not on the raw integration name: a LAAPI
  // connector is the same platform as its native twin, so `LAAPI_Quiq` must
  // resolve to the Quiq mark instead of falling through to the brand icon.
  const key = PLATFORM_LOGO_KEYS[stripLaapiPrefix(agentRouting).toLowerCase()];
  // Fall back to the Pypestream brand icon for platforms without a dedicated
  // logo (e.g. external/middleware integrations), rather than the contact-center
  // mark which implied a specific platform.
  return (key && logos[key]) || logos.pype;
};

// `isContactCenterPlatform` lives in `platform-catalog`, next to the
// supported-platform rule that uses it, and is NOT re-exported from here: this
// module reads `logos` off the design system, so a second import path through it
// would drag that dependency back into module-load for anyone reaching the
// predicate (the reason PLATFORM_LOGO_KEYS holds keys rather than values).
